type RowKey = string | number;

export interface RecyclerItem<T> {
    index: number;
    key: RowKey;
    data: T;
    /** altura medida o estimada */
    height?: number;
}

/**
 * Contexto de renderizado para un componente reciclador
 * @template T Tipo de los datos de los ítems.
 */
export interface RecyclerRenderContext<T> {
    /** Lista de ítems visibles actualmente en el reciclador */
    items: RecyclerItem<T>[];
    /** Índice del primer ítem visible */
    startIndex: number;
    /** Índice del último ítem visible */
    endIndex: number;
    /** Desplazamiento en píxeles desde la parte superior del contenedor */
    offsetTop: number;
}

export interface RecyclerManagerOptions<T> {
    /** Número que representa la altura estimada (en píxeles) de cada fila en la lista.
     *
     * Se utiliza para calcular el espacio necesario y optimizar el renderizado cuando no se conoce la altura real de cada elemento.
     * */
    estimatedRowHeight: number;
    /** Indica cuántas filas adicionales (antes y después de la vista visible) deben renderizarse.
     *
     * Ayuda a un desplazamiento más fluido, ya que algunos elementos ya están preparados antes de que el usuario los vea. */
    buffer: number;
    /** Identificador de fila con respecto al dato */
    getKey: (item: T, index: number) => RowKey;
}

/**
 * Clase que administra una lista de elementos y sus alturas para renderizado eficiente en listas virtualizadas.
 * Permite calcular el rango de elementos visibles según el scroll y el tamaño del viewport, optimizando el renderizado.
 *
 * @typeParam T - Tipo de los elementos que administra el RecyclerManager.
 *
 * @example
 * ```typescript
 * interface RowData {
 *   id: string;
 *   text: string;
 * }
 *
 * const manager = new RecyclerManager<RowData>({
 *   estimatedRowHeight: 40,
 *   buffer: 5,
 *   getKey: (item) => item.id,
 * });
 *
 * manager.setItems([
 *   { id: '1', text: 'Fila 1' },
 *   { id: '2', text: 'Fila 2' },
 *   // ...
 * ]);
 *
 * // Actualiza la altura de una fila específica
 * manager.setRowHeight('1', 42);
 *
 * // Obtiene el contexto de renderizado para un scroll dado
 * const context = manager.getRenderContext(100, 300);
 *
 * console.log(context.items); // Elementos a renderizar en el viewport actual!!
 * ```
 */
export class VirtualScroller<T> {
    private items: T[];
    private readonly rowHeights: Map<RowKey, number>;
    private readonly offsetCache: number[];
    private readonly estimatedRowHeight: number;
    private readonly buffer: number;
    private readonly getKey: (item: T, index: number) => RowKey;

    constructor(options: RecyclerManagerOptions<T>) {
        this.items = [];
        this.rowHeights = new Map();
        this.offsetCache = [];
        this.estimatedRowHeight = options.estimatedRowHeight;
        this.buffer = options.buffer;
        this.getKey = options.getKey;
    }

    // **********************************************************************
    // PUBLIC METHODS
    // **********************************************************************

    public setItems(items: T[]): void {
        this.items = items;
        this.buildOffsetCache();
    }

    public setRowHeight(key: RowKey, height: number): void {
        this.rowHeights.set(key, height);
        this.buildOffsetCache();
    }

    public getRowHeight(key: RowKey): number {
        return this.rowHeights.get(key) ?? this.estimatedRowHeight;
    }

    public getRowHeightByIndex(index: number): number {
        return Array.from(this.rowHeights.values())[index] || 0;
    }

    public getTotalHeight(): number {
        let total = 0;
        for (let i = 0; i < this.items.length; i++) {
            const key = this.getKey(this.items[i], i);
            total += this.getRowHeight(key);
        }
        return total;
    }

    // /** Saber cuántas filas caben en pantalla */
    // public getVisibleRowCount(viewportHeight: number): number {
    //     return Math.ceil(viewportHeight / this.estimatedRowHeight) + this.buffer * 2;
    // }

    /** Retorna -1 si no encuentra el ID */
    public getOffsetRow(key: RowKey): number {
        const rowIndex = this.items.findIndex((d, i) => (this.getKey(d, i) == key));
        return this.getOffsetRowByIndex(rowIndex);
    }

    /** Retorna -1 si no encuentra el indice */
    public getOffsetRowByIndex(index: number): number {
        const offset = this.offsetCache[index];
        return offset != null ? offset : -1;
    }

    /**
     * Ajusta el cálculo de índices y offset según la altura real máxima de scroll soportada por el navegador.
     * Si el scroll real es menor al total, escala el scrollTop proporcionalmente y también el offsetTop de retorno.
     * @param scrollTop posición actual del scroll
     * @param viewportHeight altura visible del contenedor
     * @param realScrollHeight altura máxima de scroll soportada por el navegador
     */
    public getRenderContextWithRealScroll(
        scrollTop: number,
        viewportHeight: number,
        realScrollHeight: number
    ): RecyclerRenderContext<T> {
        const totalHeight = this.getTotalHeight();
        let scaledScrollTop = scrollTop;
        let scale = 1;

        // Si el scroll real es menor al total, escala el scrollTop y el offsetTop
        if (realScrollHeight < totalHeight && realScrollHeight > 0) {
            scale = realScrollHeight / totalHeight;
            scaledScrollTop = (scrollTop / realScrollHeight) * totalHeight;
        }

        // Obtiene el contexto real
        const context = this.getRenderContext(scaledScrollTop, viewportHeight);

        // Ajusta el offsetTop para que sea proporcional al scroll real
        let realOffsetTop = context.offsetTop;
        if (scale !== 1) {
            realOffsetTop = context.offsetTop * scale;
        }

        return {
            ...context,
            offsetTop: realOffsetTop,
        };
    }

    // **********************************************************************
    // PRIVATE METHODS
    // **********************************************************************

    /**
     * Calcula el rango de items visibles y el offset superior para el scroll actual.
     * @param scrollTop posición actual del scroll
     * @param viewportHeight altura visible del contenedor
     */
    private getRenderContext(scrollTop: number, viewportHeight: number): RecyclerRenderContext<T> {
        // offsetTop: Desplazamiento vertical (en píxeles) desde la parte superior.
        //          Se usa para posicionar el contenedor de items en la una parte visible.
        //
        // accHeight: Es la altura acumulada de los elementos procesados hasta el momento.
        //          Sirve para calcular el espacio total ocupado o para determinar cuándo dejar de renderizar elementos según el espacio disponible.

        let startIndex = 0; // this.findStartIndex(scrollTop);
        let startIndexInViewport = 0;
        let offsetTop = 0; // this.offsetCache[startIndex] ?? 0;
        let accHeight = 0;

        // Encuentra el primer índice visible
        for (let i = 0; i < this.items.length; i++) {
            accHeight = this.offsetCache[i];
            startIndexInViewport = i;
            startIndex = Math.max(0, i - this.buffer);
            offsetTop = this.offsetCache[startIndex]; // accHeight;
            if (accHeight + this.estimatedRowHeight > scrollTop) {
                break;
            }
        }

        // Encuentra el último índice visible V1
        let endIndex = startIndexInViewport;
        // let endIndexInViewport = startIndexInViewport;
        let visibleHeight = 0;
        for (let i = startIndexInViewport; i < this.items.length; i++) {
            const key = this.getKey(this.items[i], i);
            const rowHeight = this.getRowHeight(key);
            visibleHeight += rowHeight;
            // endIndexInViewport = i;
            if (visibleHeight > viewportHeight) {
                endIndex = Math.min(this.items.length - 1, i + this.buffer); // i;
                break;
            }
            endIndex = Math.min(this.items.length - 1, i + this.buffer);
        }
        // console.table({ startIndex, startIndexInViewport, accHeight, endIndexInViewport, endIndex, offsetTop, })

        // Prepara los items a renderizar
        const renderItems: RecyclerItem<T>[] = [];
        for (let i = startIndex; i <= endIndex && i < this.items.length; i++) {
            const item = this.items[i];
            const key = this.getKey(item, i);
            renderItems.push({
                index: i,
                key,
                data: item,
                height: this.rowHeights.get(key),
            });
        }

        return {
            items: renderItems,
            startIndex,
            endIndex,
            offsetTop,
        };
    }

    private buildOffsetCache(): void {
        this.offsetCache.length = 0;
        let acc = 0;
        for (let i = 0; i < this.items.length; i++) {
            this.offsetCache[i] = acc;
            acc += this.getRowHeight(this.getKey(this.items[i], i));
        }
    }

    // private findStartIndex(scrollTop: number): number {
    //     let low = 0, high = this.items.length - 1; // definen el rango de búsqueda (índices de la lista)
    //     let acc = 0; //  acumula la altura total de las filas desde el inicio hasta un índice dado

    //     while (low <= high) {
    //         const mid = Math.floor((low + high) / 2);
    //         acc = 0;
    //         for (let i = 0; i < mid; i++) {
    //             acc += this.offsetCache[i] // this.getRowHeight(this.getKey(this.items[i], i));
    //         }

    //         if (acc < scrollTop) { // si acc es menor que scrollTop; entonces el indice está más abajo en la lista
    //             low = mid + 1;
    //         } else {
    //             high = mid - 1;
    //         }
    //     }

    //     return Math.max(0, low - this.buffer);
    // }

}

export type ScrollBlock = "start"; // "center" | "end" | "start" | "nearest";